import { useRef, type PropsWithChildren, type ReactNode } from 'react';
import { HudCorners } from '../effects/HudCorners.js';
import { useResizableTerminal } from '../../hooks/useResizableTerminal.js';

const TICKER_TEXT =
  '◤ PMR-SEC-NET ◢ ENCRYPTION: AES-256 ◢ NODE: IL-JLM-013 ◢ UPLINK STABLE ◢ CHANNEL LOCKED ◢ ';

/**
 * מעטפת הטרמינל — חלון רטרו הניתן להגדלה/הקטנה/שינוי גודל בהשראת
 * pranx.com/hacker: כפתורי חלון פונקציונליים (אדום=איפוס, ענבר=מזעור,
 * ירוק=מסך מלא) + ידית גרירה בפינה לשינוי גודל חופשי (עכבר ומגע).
 */
export function TerminalShell({
  children,
  animationsEnabled,
  header,
  ticker,
  className = '',
}: PropsWithChildren<{
  animationsEnabled: boolean;
  header?: ReactNode;
  ticker?: string;
  className?: string;
}>) {
  const tickerText = ticker ?? TICKER_TEXT;
  const shellRef = useRef<HTMLDivElement | null>(null);
  const resize = useResizableTerminal(shellRef);

  const inlineStyle =
    !resize.maximized && (resize.width !== null || resize.height !== null)
      ? {
          width: resize.width !== null ? `${resize.width}px` : undefined,
          height: resize.height !== null ? `${resize.height}px` : undefined,
          maxWidth: 'none',
        }
      : undefined;

  const shellClass = [
    'crt-shell',
    resize.maximized ? 'crt-shell--maximized' : '',
    resize.minimized ? 'crt-shell--minimized' : '',
    resize.dragging ? 'crt-shell--dragging' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={shellRef} className={shellClass} style={inlineStyle}>
      {animationsEnabled && <div className="crt-noise" aria-hidden="true" />}
      <div className="term-titlebar">
        <span className="term-titlebar__dots">
          <button
            type="button"
            className="term-titlebar__dotbtn"
            onClick={resize.reset}
            aria-label="אפס גודל חלון"
            title="איפוס גודל"
          >
            <span className="term-titlebar__dot term-titlebar__dot--red" />
          </button>
          <button
            type="button"
            className="term-titlebar__dotbtn"
            onClick={resize.toggleMinimize}
            aria-label={resize.minimized ? 'הצג תוכן' : 'מזער'}
            title={resize.minimized ? 'הצג תוכן' : 'מזער'}
          >
            <span className="term-titlebar__dot term-titlebar__dot--amber" />
          </button>
          <button
            type="button"
            className="term-titlebar__dotbtn"
            onClick={resize.toggleMaximize}
            aria-label={resize.maximized ? 'שחזר גודל' : 'הגדל למסך מלא'}
            title={resize.maximized ? 'שחזר גודל' : 'הגדל למסך מלא'}
          >
            <span className="term-titlebar__dot term-titlebar__dot--green" />
          </button>
        </span>
        <span className="term-titlebar__title mono">root@pmr-secure:~</span>
      </div>
      <div className="hud-ticker" aria-hidden="true">
        <span className="hud-ticker__track">
          {tickerText}
          {tickerText}
        </span>
      </div>
      {header}
      <div className={`term-body-scroll ${resize.minimized ? 'term-body-hidden' : ''}`}>
        <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
      </div>
      <HudCorners />
      {!resize.maximized && !resize.minimized && (
        <div
          className="term-resize-handle"
          onPointerDown={resize.startResize}
          role="separator"
          aria-orientation="horizontal"
          aria-label="גרור לשינוי גודל הטרמינל"
          tabIndex={-1}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path
              d="M12 2 2 12M12 7 7 12M12 12h.01"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}
    </div>
  );
}
